/* Three shells: the staff console (permission-driven side nav), the student and parent portals
   (phone-first bottom nav), and the plain public/auth frame. */
import { useEffect, useState, type ReactNode } from 'react';
import { api, API_MODE } from '../api';
import { Link, useRouter } from '../app/router';
import { useSession } from '../app/session';
import { NAV_GROUPS, routes, type RouteDef } from '../app/routes';
import { Icon } from '../components/icons';
import { Modal } from '../components/overlay';
import { Avatar, Button, IconButton } from '../components/ui';
import { useApi } from '../hooks/data';
import { maskMobile } from '../lib/format';

function DemoBanner() {
  if (API_MODE !== 'mock') return null;
  return <div className="demo-banner">Demo mode — every screen runs on built-in dummy data. Set <code>VITE_API_MODE=live</code> to use the FastAPI backend.</div>;
}

export function HelpdeskButton({ dark }: { dark?: boolean }) {
  const [open, setOpen] = useState(false);
  const info = useApi(() => api.support.contactInfo(), [open], { skip: !open });
  return (
    <>
      <button className="icon-btn" style={dark ? { color: '#fff' } : undefined} title="Helpdesk" aria-label="Helpdesk" onClick={() => setOpen(true)}>
        <Icon name="lifebuoy" />
      </button>
      <Modal open={open} title="Helpdesk" onClose={() => setOpen(false)}>
        {info.data ? (
          <div className="stack">
            <div className="row"><Icon name="phone" /> <strong>{String(info.data.helpdesk_phone ?? '—')}</strong></div>
            <div className="row"><Icon name="mail" /> <a href={`mailto:${String(info.data.helpdesk_email ?? '')}`}>{String(info.data.helpdesk_email ?? '—')}</a></div>
            <div className="row"><Icon name="clock" /> Support hours: {String(info.data.hours ?? '24x7')}</div>
            <div className="small muted">Response within {String(info.data.response_sla_minutes)} minutes, resolution within {String(Number(info.data.resolution_sla_minutes) / 60)} hours.</div>
          </div>
        ) : <div className="muted">Loading…</div>}
      </Modal>
    </>
  );
}

/** DPDP Act notice: shown once per notice version until the user acknowledges it. */
function PrivacyNotice() {
  const { me, refresh } = useSession();
  const [busy, setBusy] = useState(false);
  if (!me || me.privacy_notice_acknowledged) return null;
  return (
    <Modal open title="How the hostel uses your information" onClose={() => undefined}
      footer={<Button variant="primary" loading={busy} onClick={async () => { setBusy(true); try { await api.portal.acknowledgeNotice(); await refresh(); } finally { setBusy(false); } }}>I have read this</Button>}>
      <div className="stack small">
        <p>ACGCET Hostel keeps the details needed to run the hostel: identity and contact details, room allotment, fees and payments, leave and gate movements, mess records, grievances and certificates.</p>
        <p>It is used only by authorised hostel and college staff for hostel administration, is stored on servers in India, and is never sold or used for advertising. Parents see their own child's records only.</p>
        <p>You can ask the warden to correct wrong details. Records are kept for the period the college's rules require and then deleted.</p>
        <p className="muted">Notice version {me.privacy_notice_version} · Digital Personal Data Protection Act, 2023</p>
      </div>
    </Modal>
  );
}

function NavLinks({ list, onPick }: { list: RouteDef[]; onPick?: () => void }) {
  return (
    <>
      {NAV_GROUPS.map((g) => {
        const items = list.filter((r) => r.nav?.group === g);
        if (!items.length) return null;
        return (
          <div className="nav-group" key={g}>
            <div className="nav-group-title">{g}</div>
            {items.map((r) => (
              <Link key={r.path} to={r.path} activeClassName="active" exact={r.nav?.exact} onClick={onPick}>
                <Icon name={r.nav!.icon} />{r.nav!.label ?? r.title}
              </Link>
            ))}
          </div>
        );
      })}
    </>
  );
}

export function StaffShell({ children }: { children: ReactNode }) {
  const { me, can, logout } = useSession();
  const [open, setOpen] = useState(false);
  const { loc } = useRouter();
  useEffect(() => setOpen(false), [loc.pathname]);
  const visible = routes.filter((r) => r.shell === 'staff' && r.nav && (!r.perm || can(...r.perm)));
  return (
    <div className="shell">
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="brand">
          <div className="brand-mark">A</div>
          <div><div className="brand-name">ACGCET Hostel</div><div className="brand-sub">Karaikudi · Staff console</div></div>
        </div>
        <nav className="nav" aria-label="Main"><NavLinks list={visible} /></nav>
      </aside>
      <div className="main">
        <DemoBanner />
        <header className="topbar">
          <span className="menu-toggle"><IconButton icon="menu" title="Menu" onClick={() => setOpen(!open)} /></span>
          <div className="spacer" />
          <Link to="/support" title="Support"><Icon name="message" /></Link>
          <HelpdeskButton />
          <Link to="/account" className="row" title="My account">
            <Avatar name={me?.profile.name ?? me?.username} />
            <span className="small" style={{ lineHeight: 1.2, color: 'var(--ink-700)' }}>
              <strong>{me?.profile.name ?? me?.username}</strong><br /><span className="muted">{me?.roles.join(', ')}</span>
            </span>
          </Link>
          <IconButton icon="logout" title="Sign out" onClick={() => void logout()} />
        </header>
        <main className="content">{children}</main>
        <PrivacyNotice />
      </div>
    </div>
  );
}

export function PortalShell({ children, kind }: { children: ReactNode; kind: 'student' | 'parent' }) {
  const { me, logout } = useSession();
  const tabs = kind === 'student'
    ? [{ to: '/me', icon: 'home', label: 'Home', exact: true }, { to: '/me/fees', icon: 'wallet', label: 'Fees' },
       { to: '/me/leave', icon: 'plane', label: 'Leave' }, { to: '/me/menu', icon: 'utensils', label: 'Menu' }, { to: '/me/more', icon: 'more', label: 'More' }]
    : [{ to: '/parent', icon: 'home', label: 'Home', exact: true }, { to: '/parent/leave', icon: 'plane', label: 'Leave' },
       { to: '/parent/fees', icon: 'wallet', label: 'Fees' }, { to: '/parent/mess-bills', icon: 'receipt', label: 'Mess' }, { to: '/parent/notifications', icon: 'bell', label: 'Alerts' }];
  return (
    <div className="portal">
      <DemoBanner />
      <header className="portal-top">
        <Avatar name={me?.profile.name} />
        <div className="who" style={{ flex: 1 }}>
          {me?.profile.name}
          <small>{kind === 'student' ? 'Student' : 'Parent'} · {maskMobile(me?.username?.replace(/^\D+/, '') || '')}</small>
        </div>
        <HelpdeskButton dark />
        <button className="icon-btn" style={{ color: '#fff' }} title="Sign out" aria-label="Sign out" onClick={() => void logout()}><Icon name="logout" /></button>
      </header>
      <main className="portal-body">{children}</main>
      <PrivacyNotice />
      <nav className="bottom-nav" aria-label="Portal">
        {tabs.map((t) => (
          <Link key={t.to} to={t.to} activeClassName="active" exact={t.exact}><Icon name={t.icon} />{t.label}</Link>
        ))}
      </nav>
    </div>
  );
}

export function PublicFrame({ children }: { children: ReactNode }) {
  return (
    <div className="portal">
      <DemoBanner />
      <header className="portal-top"><div className="brand-mark">A</div><div className="who">ACGCET Hostel<small>Alagappa Chettiar Government College of Engineering and Technology, Karaikudi</small></div></header>
      <main className="portal-body">{children}</main>
    </div>
  );
}

export function NotAllowed() {
  const { home } = useSession();
  return (
    <div className="card card-body" style={{ maxWidth: 520 }}>
      <h2>You do not have access to this page</h2>
      <p className="muted" style={{ marginTop: 8 }}>Ask the Super Admin for the permission if you need it.</p>
      <div className="mt"><Link to={home()}><Button variant="primary">Go to my home page</Button></Link></div>
    </div>
  );
}
